import React, { useState, useContext, ContextType } from "react";
import Modal from "./Modal";
import { Context } from "..";

type BudgetFormModalProps = {
  onClose: () => void;
  updateObj?: null | ContextType<typeof Context>["budgets"][0][0];
};

const BudgetFormModal: React.FC<BudgetFormModalProps> = ({
  onClose,
  updateObj,
}) => {
  const [attachTo, setAttachTo] = useState<"CATEGORY" | "EVENT">(
    updateObj?.attachedTo.type ?? "CATEGORY"
  );
  const [newBudgetValue, setNewBudgetValue] = useState(
    typeof updateObj?.value === "number" ? updateObj?.value + "" : "100"
  );
  const [newBudgetName, setNewBudgetName] = useState(updateObj?.name ?? "");
  const [newBudgetSpan, setNewBudgetSpan] = useState<"MONTHLY" | "YEARLY">(
    updateObj?.span ?? "MONTHLY"
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    updateObj?.attachedTo.type === "CATEGORY" ? updateObj?.attachedTo.id : "*"
  );
  const [selectedEvent, setSelectedEvent] = useState<string | null>(
    updateObj?.attachedTo.type === "EVENT" ? updateObj?.attachedTo.id : null
  );
  const [newMustIncludePhrase, setNewMustIncludePhrase] = useState(
    updateObj?.containing ?? ""
  );
  const { budgets, events, expenseCategories } = useContext(Context);

  const getNameByCategoryOrEvent = () => {
    if (attachTo === "CATEGORY") {
      return (
        expenseCategories[0].find((ec) => ec.id === selectedCategory)?.label ||
        "-"
      );
    }
    if (attachTo === "EVENT") {
      return events[0].find((e) => e.id === selectedEvent)?.name || "-";
    }
    return "-";
  };

  const upsertBudget = () => {
    const convertedValue = parseInt(newBudgetValue);

    if (isNaN(convertedValue)) {
      window.alert("Invalid value for budget. Problem at converting value");
      return;
    }

    const attachToId = attachTo === "EVENT" ? selectedEvent : selectedCategory;

    if (attachToId === null) {
      window.alert("AttachToId is null");
      return;
    }

    if (updateObj) {
      budgets[1]((budgets) => {
        const draft = [...budgets];
        const foundIndex = draft.findIndex((b) => b.id === updateObj.id);

        if (foundIndex === -1) {
          return draft;
        }

        draft[foundIndex] = {
          ...draft[foundIndex],
          name:
            newBudgetName.length > 0
              ? newBudgetName
              : getNameByCategoryOrEvent(),
          value: convertedValue,
          span: newBudgetSpan,
          attachedTo: {
            type: attachTo,
            id: attachToId,
          },
          ...(newMustIncludePhrase.length > 0
            ? {
                containing: newMustIncludePhrase,
              }
            : {}),
        };

        return draft;
      });
    } else {
      budgets[1]((budgets) => [
        {
          id: Math.round(Math.random() * 100000000) + "",
          name:
            newBudgetName.length > 0
              ? newBudgetName
              : getNameByCategoryOrEvent(),
          value: convertedValue,
          span: newBudgetSpan,
          attachedTo: {
            type: attachTo,
            id: attachToId,
          },
          ...(newMustIncludePhrase.length > 0
            ? {
                containing: newMustIncludePhrase,
              }
            : {}),
        },
        ...budgets,
      ]);
    }

    onClose();
    setNewBudgetValue("100");
    setNewBudgetName("");
    setNewBudgetSpan("MONTHLY");
  };

  const [phraseModalOpened, setPhraseModalOpened] = useState(false);

  const formValid =
    (attachTo === "CATEGORY" && selectedCategory !== null) ||
    (attachTo === "EVENT" && selectedEvent !== null);

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="text-3xl">
          {updateObj ? "Update Budget" : "Add Budget"}
        </div>
        <div className="my-4">
          <div>Budget Name</div>
          <input
            placeholder="Budget name"
            type="text"
            className="rounded-md border-gray-400 px-2 py-1 w-full sm:w-6/12"
            value={newBudgetName}
            onChange={(e) => setNewBudgetName(e.target.value)}
          />
        </div>
        <div className="my-4">
          <div>
            Value{" "}
            <span className="text-sm text-gray-400">
              (Expenses shouldn't exceed this value)
            </span>
          </div>
          <input
            type="text"
            className="rounded-md border-gray-400 px-2 py-1 w-full sm:w-6/12"
            value={newBudgetValue}
            onChange={(e) => setNewBudgetValue(e.target.value)}
          />
        </div>

        <div className="mt-6 mb-4">
          <div className="inline">Attach to</div>
          <select
            value={attachTo}
            onChange={(e) =>
              setAttachTo(e.target.value as "CATEGORY" | "EVENT")
            }
            className="rounded-md border border-gray-400 px-2 py-2 ml-3"
          >
            <option value="CATEGORY">Category</option>
            <option value="EVENT">Event</option>
          </select>
        </div>
        <div>
          Choose {attachTo === "CATEGORY" ? "category:" : "event:"}
          {attachTo === "CATEGORY" ? (
            <select
              className="rounded-md border border-gray-400 px-2 py-2 ml-3"
              value={selectedCategory || ""}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option key="*" value="*">
                All categories
              </option>
              {expenseCategories[0].map((et) => (
                <option key={et.id} value={et.id}>
                  {et.label}
                </option>
              ))}
            </select>
          ) : (
            <select
              className="rounded-md border border-gray-400 px-2 py-2 ml-3"
              value={selectedEvent || ""}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              <option key="-" value="">
                -
              </option>
              {events[0].map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {attachTo === "CATEGORY" && (
          <div className="my-4">
            <div>Budget term</div>
            <select
              value={newBudgetSpan}
              onChange={(e) =>
                setNewBudgetSpan(e.target.value as "MONTHLY" | "YEARLY")
              }
              className="rounded-md border border-gray-400 px-2 py-2 w-full sm:w-6/12 "
            >
              <option value="MONTHLY">Month</option>
              <option value="YEARLY">Year</option>
            </select>
          </div>
        )}
        <div className="my-4">
          <div>Must include phrase:</div>
          <div className="flex items-center">
            <input
              value={newMustIncludePhrase}
              onChange={(e) => setNewMustIncludePhrase(e.target.value)}
              className="rounded-md border border-gray-400 px-2 py-2 w-full sm:w-6/12 "
            />
            <div
              onClick={() => setPhraseModalOpened(true)}
              className="ml-4 bg-gray-800 text-white w-8 h-8 flex items-center justify-center rounded-full cursor-pointer hover:bg-gray-600 transition-colors duration-500 font-light"
            >
              ?
            </div>
            {phraseModalOpened && (
              <Modal onClose={() => setPhraseModalOpened(false)}>
                <div className="p-10 flex flex-col items-center justify-center">
                  <div className="w-6/12">
                    If phrase is set, then budget takes only expenses where
                    description contains that particular phrase. For example
                    phrase "alcohol" will accumulate only expenses that contains
                    text "alcohol" in their description
                  </div>
                  <div className="w-6/12 mt-4">
                    This is useful if you want to distinguish between
                    transactions. One transaction can be related to friend,
                    coffee, alcohol or something else and you want to limit one
                    of these types.
                  </div>
                </div>
              </Modal>
            )}
          </div>
        </div>
        <div className="mt-10">
          <button
            disabled={!formValid}
            onClick={upsertBudget}
            className={`px-16 py-2  text-gray-50 rounded-full mr-4 w-full sm:w-auto ${
              formValid ? "bg-green-600" : "bg-gray-500 cursor-not-allowed"
            }`}
          >
            {updateObj ? "Update" : "Add"}
          </button>
          <button
            onClick={onClose}
            className="mt-4 sm:mt-0 px-10 py-2 rounded-full mr-4 border border-gray-400 outline-none focus:outline-none w-full sm:w-auto"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BudgetFormModal;
